import React, { useEffect, useState } from 'react';
import { maskEmail } from '../shared/adminEmail2fa';

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 6);
}

/**
 * Code entry after a Super Admin password is accepted.
 * No icons. The field is at least 16px so iPhone does not zoom.
 */
function AdminEmailCode({
  email,
  colors,
  error,
  busy,
  retryAfterSeconds,
  retryNonce,
  onSubmit,
  onResend,
  onLogout,
  previewSafeTop,
}) {
  const ink = colors || {};
  const [code, setCode] = useState('');
  const [wait, setWait] = useState(retryAfterSeconds || 0);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const safeTop = previewSafeTop || 'env(safe-area-inset-top, 0px)';

  useEffect(() => {
    setWait(retryAfterSeconds || 0);
  }, [retryAfterSeconds, retryNonce]);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const id = window.setTimeout(() => setWait(wait - 1), 1000);
    return () => window.clearTimeout(id);
  }, [wait]);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const submit = (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (busy || code.length !== 6) return;
    onSubmit(code);
  };

  const buttonBg = ink.text || '#1d1d1f';
  const buttonFg = ink.background || '#ffffff';

  return (
    <div
      className="app-scroll"
      style={{
        minHeight: '100vh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: narrow ? 'flex-start' : 'center',
        background: ink.background || '#ffffff',
        color: ink.text || '#1d1d1f',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        paddingTop: `calc(${safeTop} + ${narrow ? '2.75rem' : '1.5rem'})`,
        paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
        paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
      }}
    >
      <form
        onSubmit={submit}
        style={{ width: '100%', maxWidth: '22.5rem', minWidth: 0, boxSizing: 'border-box' }}
      >
        <p style={{
          margin: '0 0 0.75rem',
          fontSize: '0.8125rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: ink.textMuted || '#6e6e73',
        }}
        >
          Pacarion
        </p>
        <h1 style={{
          margin: '0 0 0.75rem',
          fontSize: narrow ? '2rem' : '2.125rem',
          lineHeight: 1.15,
          fontWeight: 600,
          letterSpacing: '-0.02em',
        }}
        >
          Enter code
        </h1>
        <p id="admin-code-hint" style={{
          margin: '0 0 1.75rem',
          fontSize: '1.0625rem',
          lineHeight: 1.45,
          color: ink.textSecondary || '#3a3a3c',
        }}
        >
          We sent a 6-digit code to {maskEmail(email)}. It expires in 10 minutes.
        </p>
        <div style={{ position: 'relative', margin: '0 0 0.75rem' }}>
          <div aria-hidden="true" style={{ display: 'flex', gap: '0.35rem', width: '100%', minWidth: 0 }}>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <div
                key={index}
                style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  height: '3.25rem',
                  borderRadius: '0.75rem',
                  border: `1px solid ${ink.borderLight || '#d2d2d7'}`,
                  background: ink.surface || '#ffffff',
                  color: ink.text || '#1d1d1f',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  lineHeight: '3.25rem',
                  textAlign: 'center',
                  fontFamily: 'inherit',
                  overflow: 'hidden',
                }}
              >
                {code[index] || ''}
              </div>
            ))}
          </div>
          <label htmlFor="admin-email-code" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            Sign-in code
          </label>
          <input
            id="admin-email-code"
            name="one-time-code"
            value={code}
            onChange={(event) => setCode(digitsOnly(event.target.value))}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            pattern="[0-9]*"
            maxLength={6}
            enterKeyHint="done"
            aria-describedby="admin-code-hint"
            aria-invalid={error ? 'true' : 'false'}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: 'transparent',
              caretColor: 'transparent',
              fontSize: '1.75rem',
              textAlign: 'center',
              fontFamily: 'inherit',
            }}
          />
        </div>
        {error ? (
          <p role="alert" style={{
            margin: '0 0 1rem',
            fontSize: '0.9375rem',
            lineHeight: 1.4,
            color: '#b42318',
          }}
          >
            {error}
          </p>
        ) : null}
        {wait > 0 ? (
          <p style={{
            margin: '0 0 1rem',
            fontSize: '0.9375rem',
            lineHeight: 1.4,
            color: ink.textSecondary || '#6e6e73',
          }}
          >
            You can request another code in {wait} {wait === 1 ? 'second' : 'seconds'}.
          </p>
        ) : <div style={{ height: '0.25rem' }} />}
        <button
          type="submit"
          disabled={busy || code.length !== 6}
          style={{
            display: 'block',
            width: '100%',
            minHeight: '3rem',
            margin: '0 0 0.75rem',
            border: 'none',
            borderRadius: '0.875rem',
            background: buttonBg,
            color: buttonFg,
            fontSize: '1.0625rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: busy || code.length !== 6 ? 'default' : 'pointer',
            opacity: busy || code.length !== 6 ? 0.35 : 1,
          }}
        >
          {busy ? 'Checking' : 'Continue'}
        </button>
        <button
          type="button"
          onClick={onResend}
          disabled={busy || wait > 0}
          style={{
            display: 'block',
            width: '100%',
            minHeight: '2.75rem',
            border: 'none',
            borderRadius: '0.875rem',
            background: 'transparent',
            color: ink.text || '#1d1d1f',
            fontSize: '1.0625rem',
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: busy || wait > 0 ? 'default' : 'pointer',
            opacity: busy || wait > 0 ? 0.4 : 1,
          }}
        >
          Resend code
        </button>
        <button
          type="button"
          onClick={onLogout}
          style={{
            display: 'block',
            width: '100%',
            minHeight: '2.75rem',
            marginTop: '0.5rem',
            border: 'none',
            background: 'transparent',
            color: ink.textSecondary || '#6e6e73',
            fontSize: '1rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Log out
        </button>
      </form>
    </div>
  );
}

export default AdminEmailCode;
